import { Link } from 'react-router-dom';
import { css, html } from 'react-strict-dom';
import { color } from 'src/constants/color.css';

import Text from '../../../components/Text';
import { Model } from '../../../models';
import { formatReportMinutes } from '../util';
import ReportStatusTag from './ReportStatusTag';

type Props = {
  href: string;
  number: number;
  title: string;
  status: { label: string; variant: Model.Ticket.ReportStatusVariant };
  minutes: number;
};

/**
 * One ticket in a report list.  The whole row links to the ticket; only hover
 * shows it, so a list of thirty-five rows does not read as a wall of blue.
 */
export default function ReportSummaryRow({
  href,
  number,
  title,
  status,
  minutes,
}: Props) {
  const hasTime = minutes > 0;
  return (
    <Link to={href} {...css.props(styles.link)}>
      <html.div style={styles.row}>
        <html.div style={styles.titleLine}>
          <Text typeface="body.small.medium" color="neutral40">
            #{number}
          </Text>
          <Text typeface="body.regular" color="neutral100" style={styles.title}>
            {title}
          </Text>
        </html.div>
        <html.div style={styles.metaLine}>
          <ReportStatusTag label={status.label} variant={status.variant} />
          <Text
            typeface="body.small.medium"
            color={hasTime ? 'neutral100' : 'neutral40'}
          >
            {formatReportMinutes(minutes)}
          </Text>
        </html.div>
      </html.div>
    </Link>
  );
}

const styles = css.create({
  link: {
    textDecoration: 'none',
    display: 'block',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingBlock: 12,
    paddingInline: 16,
    minWidth: 0,
    transition: 'background-color 0.15s',
    ':hover': {
      backgroundColor: color.neutral5,
    },
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: 6,
      paddingBlock: 12,
      paddingInline: 14,
    },
  },
  titleLine: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexGrow: 1,
    minWidth: 0,
    '@media (max-width: 768px)': {
      width: '100%',
    },
  },
  title: {
    flexShrink: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '@media (max-width: 768px)': {
      flexShrink: 0,
      overflow: 'visible',
      textOverflow: 'clip',
      whiteSpace: 'normal',
    },
  },
  metaLine: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexShrink: 0,
    '@media (max-width: 768px)': {
      width: '100%',
      paddingInlineStart: 38,
    },
  },
});
